import { useMemo, useState } from "react";
import { Play } from "lucide-react";
import { Lightbox } from "./Lightbox";
import { SmartImg } from "./SmartImg";
import { thumbUrl } from "../lib/api";

const entryImageIds = (e) => [
  ...(e.photo_id ? [e.photo_id] : []),
  ...(e.media || []).filter((m) => m.content_type?.startsWith("image")).map((m) => m.id),
];

export const JournalPhotoView = ({ groups, other = [], mediaUrl, t, locale, dayLabel }) => {
  const [viewer, setViewer] = useState(null);
  const sections = useMemo(() => {
    const secs = groups
      .map((g) => ({ day: g.day, photos: g.entries.flatMap((e) => entryImageIds(e).map((id) => ({ id, e }))) }))
      .filter((s) => s.photos.length > 0)
      .reverse();
    const oth = [...other].reverse().flatMap((e) => entryImageIds(e).map((id) => ({ id, e })));
    if (oth.length) secs.push({ day: null, photos: oth });
    return secs;
  }, [groups, other]);
  const flat = useMemo(() => sections.flatMap((s) => s.photos.map((p) => ({ ...p, day: s.day }))), [sections]);
  if (flat.length === 0) {
    return <p className="text-sm text-muted-foreground bg-card rounded-2xl p-5" data-testid="journal-photos-empty">{t("noPhotos")}</p>;
  }
  const urls = flat.map((p) => mediaUrl(p.id));
  const captions = flat.map((p) => {
    const bits = [];
    if (p.day) bits.push(`${t("day")} ${p.day.day_number}`);
    if (p.e.location_name) bits.push(p.e.location_name.split(",")[0]);
    if (p.e.timestamp) bits.push(new Date(p.e.timestamp).toLocaleDateString(locale, { day: "numeric", month: "short" }));
    return bits.join(" · ");
  });
  const indexOf = new Map(flat.map((p, i) => [p.id, i]));
  return (
    <div data-testid="journal-photo-view">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-xs text-muted-foreground">{flat.length} {t("photosWord")}</p>
        <button data-testid="journal-slideshow-btn" onClick={() => setViewer({ index: 0, autoplay: true })}
          className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-bold px-4 py-2 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
          <Play className="w-3.5 h-3.5" /> {t("slideshow")}
        </button>
      </div>
      <div className="space-y-6">
        {sections.map((s) => (
          <div key={s.day ? s.day.day_number : "other"} data-testid={`journal-photos-day-${s.day ? s.day.day_number : "other"}`}>
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
              {s.day ? <>{t("day")} {s.day.day_number} · {s.day.date}</> : t("otherMoments")}
            </p>
            {s.day && dayLabel && <p className="font-heading font-bold text-sm truncate">{dayLabel(s.day)}</p>}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mt-2">
              {s.photos.map((p) => (
                <button key={p.id} type="button" data-testid={`journal-photo-tile-${p.id}`}
                  onClick={() => setViewer({ index: indexOf.get(p.id), autoplay: false })}
                  className="relative aspect-square overflow-hidden rounded-xl group bg-secondary">
                  <SmartImg src={thumbUrl(mediaUrl(p.id))} alt="Reisfoto" loading="lazy" decoding="async" retryLabel={t("photoRetry")}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {viewer && (
        <Lightbox urls={urls} captions={captions} index={viewer.index} withSlideshow autoplayStart={viewer.autoplay}
          onClose={() => setViewer(null)} />
      )}
    </div>
  );
};
